import { useState, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Shield, Home, Car, Heart, CheckCircle2, ArrowRight, ArrowLeft, User, Mail, Phone, MapPin, Calendar, DollarSign, Upload, FileText, X, File, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuotePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialType = searchParams.get("type") || "";
  const fileInputRef = useRef(null);
  
  // Skip step 1 if type is pre-selected from URL (e.g., coming from Homeowners page)
  const [step, setStep] = useState(initialType ? 2 : 1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [formData, setFormData] = useState({
    insuranceType: initialType,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    zipCode: "",
    dateOfBirth: "",
    // Renters specific
    propertyType: "",
    coverageAmount: "",
    // Homeowners specific - Basic Info
    homeValue: "",
    yearBuilt: "",
    // Homeowners specific - Property Details (from PDF questionnaire)
    propertyTypeHome: "", // Homeowners, Co-op, Condo, Dwelling
    nameOnDeed: "",
    riskAddressStreet: "",
    riskAddressCity: "",
    riskAddressCounty: "",
    riskAddressState: "",
    isPrimaryResidence: "",
    isRentedToOthers: "",
    isVacant: "",
    // Dwelling Information
    attachedOrDetached: "",
    constructionType: "", // Frame, Masonry, FR
    numberOfFamilies: "",
    numberOfBathrooms: "",
    squareFootage: "",
    numberOfFloors: "",
    roofCovering: "", // Asphalt, Tile, Slate, Rubber
    roofAge: "",
    sidingType: "",
    garageType: "", // Attached, Detached, None
    foundationType: "", // Basement, Crawlspace, Slab
    basementFinishedPercent: "",
    hasSumpPump: "",
    distanceToFireDept: "",
    distanceToHydrant: "",
    // Heating & Systems
    heatType: "", // Gas, Oil, Electric
    oilTankAge: "",
    oilTankConstruction: "", // Steel, Fiberglass
    oilTankLocation: "",
    burnerAge: "",
    hasAlternateHeatSource: "",
    alternateHeatType: "", // Wood Stove, Coal Stove, Fireplace
    electricalType: "", // Circuit Breakers, Fuses
    electricalUpdateYear: "",
    electricalUpdateType: "", // Partial, Full
    plumbingUpdateYear: "",
    plumbingUpdateType: "", // Partial, Full
    // Safety & Liability
    hasDogs: "",
    dogBreeds: "",
    hasTrampolines: "",
    hasPool: "",
    poolHasDivingBoard: "",
    poolHasSlide: "",
    hasPlannedRenovations: "",
    renovationDetails: "",
    rentalIncome: "",
    // Safety Credits
    hasLocalFireAlarm: false,
    hasCentralFireAlarm: false,
    hasGenerator: false,
    hasGasLeakDetection: false,
    hasLightningProtection: false,
    hasWaterLeakDetection: false,
    hasWaterShutoffValve: false,
    hasLocalBurglarAlarm: false,
    hasCentralBurglarAlarm: false,
    hasGatedCommunity: false,
    hasSprinklerSystem: false,
    // Loss History
    hasLossHistory: "",
    lossHistoryDetails: "",
    // Car specific
    vehicleYear: "",
    vehicleMake: "",
    vehicleModel: "",
    // Life specific
    coverageTerm: "",
    lifeCoverageAmount: "",
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [submittedQuoteId, setSubmittedQuoteId] = useState(null);

  const insuranceTypes = [
    { id: "renters", name: "Renters", icon: Home, color: "blue" },
    { id: "homeowners", name: "Homeowners", icon: Shield, color: "navy" },
    { id: "car", name: "Auto", icon: Car, color: "slate" },
    { id: "life", name: "Life", icon: Heart, color: "amber" },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTypeSelect = (type) => {
    // If Life insurance is selected, redirect to the life page with iframe
    if (type === "life") {
      navigate("/life");
      return;
    }
    setFormData(prev => ({ ...prev, insuranceType: type }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map(file => ({
      name: file.name,
      size: file.size,
      type: file.type,
      file: file
    }));
    setUploadedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const nextStep = () => setStep(prev => prev + 1);
  const prevStep = () => setStep(prev => prev - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Prepare data for API
      const quoteData = {
        insurance_type: formData.insuranceType,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        zip_code: formData.zipCode,
        date_of_birth: formData.dateOfBirth,
        
        // Renters specific
        property_type: formData.propertyType || null,
        coverage_amount: formData.coverageAmount || null,
        
        // Homeowners specific - Basic Info
        home_value: formData.homeValue || null,
        year_built: formData.yearBuilt || null,
        
        // Homeowners specific - Property Details
        property_type_home: formData.propertyTypeHome || null,
        name_on_deed: formData.nameOnDeed || null,
        risk_address_street: formData.riskAddressStreet || null,
        risk_address_city: formData.riskAddressCity || null,
        risk_address_county: formData.riskAddressCounty || null,
        risk_address_state: formData.riskAddressState || null,
        is_primary_residence: formData.isPrimaryResidence || null,
        is_rented_to_others: formData.isRentedToOthers || null,
        is_vacant: formData.isVacant || null,
        
        // Dwelling Information
        attached_or_detached: formData.attachedOrDetached || null,
        construction_type: formData.constructionType || null,
        number_of_families: formData.numberOfFamilies || null,
        number_of_bathrooms: formData.numberOfBathrooms || null,
        square_footage: formData.squareFootage || null,
        number_of_floors: formData.numberOfFloors || null,
        roof_covering: formData.roofCovering || null,
        roof_age: formData.roofAge || null,
        siding_type: formData.sidingType || null,
        garage_type: formData.garageType || null,
        foundation_type: formData.foundationType || null,
        basement_finished_percent: formData.basementFinishedPercent || null,
        has_sump_pump: formData.hasSumpPump || null,
        distance_to_fire_dept: formData.distanceToFireDept || null,
        distance_to_hydrant: formData.distanceToHydrant || null,
        
        // Heating & Systems
        heat_type: formData.heatType || null,
        oil_tank_age: formData.oilTankAge || null,
        oil_tank_construction: formData.oilTankConstruction || null,
        oil_tank_location: formData.oilTankLocation || null,
        burner_age: formData.burnerAge || null,
        has_alternate_heat_source: formData.hasAlternateHeatSource || null,
        alternate_heat_type: formData.alternateHeatType || null,
        electrical_type: formData.electricalType || null,
        electrical_update_year: formData.electricalUpdateYear || null,
        electrical_update_type: formData.electricalUpdateType || null,
        plumbing_update_year: formData.plumbingUpdateYear || null,
        plumbing_update_type: formData.plumbingUpdateType || null,
        
        // Safety & Liability
        has_dogs: formData.hasDogs || null,
        dog_breeds: formData.dogBreeds || null,
        has_trampolines: formData.hasTrampolines || null,
        has_pool: formData.hasPool || null,
        pool_has_diving_board: formData.poolHasDivingBoard || null,
        pool_has_slide: formData.poolHasSlide || null,
        has_planned_renovations: formData.hasPlannedRenovations || null,
        renovation_details: formData.renovationDetails || null,
        rental_income: formData.rentalIncome || null,
        
        // Loss History
        has_loss_history: formData.hasLossHistory || null,
        loss_history_details: formData.lossHistoryDetails || null,
        
        // Car specific
        vehicle_year: formData.vehicleYear || null,
        vehicle_make: formData.vehicleMake || null,
        vehicle_model: formData.vehicleModel || null,
        
        // Life specific
        coverage_term: formData.coverageTerm || null,
        life_coverage_amount: formData.lifeCoverageAmount || null,
        
        // Documents
        uploaded_documents: uploadedFiles.map(f => f.name),
      };

      const response = await fetch(`${BACKEND_URL}/api/quotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(quoteData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit quote request');
      }

      const result = await response.json();
      setSubmittedQuoteId(result.id);
      setSubmitted(true);
      
    } catch (error) {
      console.error('Error submitting quote:', error);
      setSubmitError('There was an error submitting your request. Please try again or call us directly at 201.227.6413');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTotalSteps = () => {
    // Returns the FINAL step number (not count of steps)
    // Life insurance doesn't need document upload step
    // Steps: (Type), Personal Info, Coverage Details, Review
    if (formData.insuranceType === "life") {
      return 4; // Final step is always 4 (review at step 4)
    }
    // Homeowners has extra detailed property info step
    // Steps: (Type), Personal Info, Property Details, Dwelling Info, Document Upload, Review
    if (formData.insuranceType === "homeowners") {
      return 6; // Final step is 6
    }
    // Renters/Car: (Type), Personal Info, Coverage Details, Document Upload, Review
    return 5; // Final step is 5
  };

  const getDocumentRequirements = () => {
    switch (formData.insuranceType) {
      case "renters":
        return {
          title: "Upload Supporting Documents",
          description: "Please upload any relevant documents to help us provide an accurate quote.",
          suggestions: [
            "Current lease agreement",
            "Inventory list of valuable items",
            "Photos of high-value belongings",
            "Previous insurance declarations page (if applicable)"
          ]
        };
      case "homeowners":
        return {
          title: "Upload Property Documents",
          description: "Help us understand your property better by uploading relevant documents.",
          suggestions: [
            "Property deed or closing documents",
            "Home inspection report",
            "Photos of your property (exterior/interior)",
            "Current insurance declarations page",
            "Roof inspection certificate (if available)"
          ]
        };
      case "car":
        return {
          title: "Upload Vehicle Documents",
          description: "Please provide documents related to your vehicle and driving history.",
          suggestions: [
            "Vehicle registration",
            "Driver's license (front and back)",
            "Current insurance declarations page",
            "Vehicle photos",
            "Driving record (if available)"
          ]
        };
      default:
        return {
          title: "Upload Documents",
          description: "Please upload any relevant supporting documents.",
          suggestions: []
        };
    }
  };

  if (submitted) {
    return (
      <div className="pt-20 min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-bold text-navy-900 mb-4">Quote Request Received!</h1>
          <p className="text-lg text-slate-600 mb-4">
            Thank you, {formData.firstName}! Your {formData.insuranceType} insurance quote request has been submitted successfully.
          </p>
          <p className="text-slate-600 mb-8">
            <strong>Bradley Greenberg</strong> will contact you within 24 hours with personalized options.
          </p>
          {submittedQuoteId && (
            <p className="text-sm text-slate-500 mb-4">
              Reference ID: <code className="bg-slate-100 px-2 py-1 rounded">{submittedQuoteId}</code>
            </p>
          )}
          {uploadedFiles.length > 0 && (
            <div className="bg-emerald-50 rounded-xl p-4 mb-6 text-left">
              <p className="text-emerald-800 font-medium mb-2">
                <CheckCircle2 className="w-5 h-5 inline mr-2" />
                {uploadedFiles.length} document{uploadedFiles.length > 1 ? 's' : ''} uploaded successfully
              </p>
              <ul className="text-sm text-emerald-700 space-y-1">
                {uploadedFiles.map((file, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    {file.name}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="bg-white rounded-2xl p-6 shadow-lg mb-8">
            <h3 className="font-semibold text-navy-900 mb-4">What happens next?</h3>
            <div className="space-y-4 text-left">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-navy-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-navy-600 font-semibold text-sm">1</span>
                </div>
                <div>
                  <p className="font-medium text-navy-900">Review Your Information</p>
                  <p className="text-sm text-slate-600">Bradley reviews your quote request and documents</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-navy-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-navy-600 font-semibold text-sm">2</span>
                </div>
                <div>
                  <p className="font-medium text-navy-900">Compare Options</p>
                  <p className="text-sm text-slate-600">We shop multiple carriers for the best rates</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-navy-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-navy-600 font-semibold text-sm">3</span>
                </div>
                <div>
                  <p className="font-medium text-navy-900">Get Your Quote</p>
                  <p className="text-sm text-slate-600">Receive personalized quotes via email or phone</p>
                </div>
              </div>
            </div>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-navy-600 text-white font-semibold rounded-full hover:bg-navy-700 transition-colors"
          >
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-600">Step {step} of {getTotalSteps()}</span>
            <span className="text-sm font-medium text-navy-600">{Math.round((step / getTotalSteps()) * 100)}% Complete</span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-navy-600 to-blue-600 transition-all duration-300"
              style={{ width: `${(step / getTotalSteps()) * 100}%` }}
            ></div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Step 1: Insurance Type */}
          {step === 1 && (
            <div className="bg-white rounded-2xl p-8 shadow-lg" data-testid="quote-step-1">
              <h2 className="text-2xl font-bold text-navy-900 mb-2">What type of insurance do you need?</h2>
              <p className="text-slate-600 mb-8">Select the coverage that best fits your needs.</p>
              
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {insuranceTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => handleTypeSelect(type.id)}
                    className={`p-6 rounded-xl border-2 transition-all text-left ${
                      formData.insuranceType === type.id
                        ? "border-navy-600 bg-navy-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`quote-type-${type.id}`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                      type.color === "blue" ? "bg-blue-100" :
                      type.color === "navy" ? "bg-navy-100" :
                      type.color === "slate" ? "bg-slate-200" :
                      "bg-amber-100"
                    }`}>
                      <type.icon className={`w-6 h-6 ${
                        type.color === "blue" ? "text-blue-600" :
                        type.color === "navy" ? "text-navy-600" :
                        type.color === "slate" ? "text-slate-700" :
                        "text-amber-600"
                      }`} />
                    </div>
                    <h3 className="font-semibold text-navy-900">{type.name} Insurance</h3>
                    {type.id === "life" && (
                      <p className="text-xs text-slate-500 mt-1">Redirects to partner site</p>
                    )}
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={nextStep}
                disabled={!formData.insuranceType}
                className="w-full py-4 bg-gradient-to-r from-navy-600 to-navy-700 text-white font-semibold rounded-xl hover:from-navy-700 hover:to-navy-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                Continue
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Step 2: Personal Information */}
          {step === 2 && (
            <div className="bg-white rounded-2xl p-8 shadow-lg" data-testid="quote-step-2">
              <h2 className="text-2xl font-bold text-navy-900 mb-2">Tell us about yourself</h2>
              <p className="text-slate-600 mb-8">We need some basic information to provide your quote.</p>
              
              <div className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">First Name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                        placeholder="John"
                        required
                        data-testid="quote-first-name"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Last Name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                        placeholder="Doe"
                        required
                        data-testid="quote-last-name"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                      placeholder="john@example.com"
                      required
                      data-testid="quote-email"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                        placeholder="(555) 123-4567"
                        required
                        data-testid="quote-phone"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">ZIP Code</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                        placeholder="78701"
                        required
                        data-testid="quote-zipcode"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Date of Birth</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                      required
                      data-testid="quote-dob"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex-1 py-4 border-2 border-slate-200 text-slate-600 font-semibold rounded-xl hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className="flex-1 py-4 bg-gradient-to-r from-navy-600 to-navy-700 text-white font-semibold rounded-xl hover:from-navy-700 hover:to-navy-800 transition-all flex items-center justify-center gap-2"
                >
                  Continue
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Coverage Details */}
          {step === 3 && (
            <div className="bg-white rounded-2xl p-8 shadow-lg" data-testid="quote-step-3">
              <h2 className="text-2xl font-bold text-navy-900 mb-2">Coverage Details</h2>
              <p className="text-slate-600 mb-8">Help us understand your coverage needs.</p>
              
              <div className="space-y-6">
                {formData.insuranceType === "renters" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Property Type</label>
                      <select
                        name="propertyType"
                        value={formData.propertyType}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                        required
                      >
                        <option value="">Select property type</option>
                        <option value="apartment">Apartment</option>
                        <option value="condo">Condo</option>
                        <option value="house">House</option>
                        <option value="townhouse">Townhouse</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Coverage Amount</label>
                      <select
                        name="coverageAmount"
                        value={formData.coverageAmount}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                        required
                      >
                        <option value="">Select coverage amount</option>
                        <option value="20000">$20,000</option>
                        <option value="30000">$30,000</option>
                        <option value="50000">$50,000</option>
                        <option value="75000">$75,000</option>
                        <option value="100000">$100,000</option>
                      </select>
                    </div>
                  </>
                )}

                {formData.insuranceType === "homeowners" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Property Type</label>
                      <select
                        name="propertyTypeHome"
                        value={formData.propertyTypeHome}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                        required
                      >
                        <option value="">Select property type</option>
                        <option value="homeowners">Homeowners (Single Family)</option>
                        <option value="coop">Co-op</option>
                        <option value="condo">Condo</option>
                        <option value="dwelling">Dwelling</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Property Address</label>
                      <input
                        type="text"
                        name="riskAddressStreet"
                        value={formData.riskAddressStreet}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                        placeholder="Street Address"
                        required
                      />
                    </div>
                    
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">City</label>
                        <input
                          type="text"
                          name="riskAddressCity"
                          value={formData.riskAddressCity}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                          placeholder="City"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">County</label>
                        <input
                          type="text"
                          name="riskAddressCounty"
                          value={formData.riskAddressCounty}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                          placeholder="County"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">State</label>
                        <input
                          type="text"
                          name="riskAddressState"
                          value={formData.riskAddressState}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                          placeholder="NJ"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Estimated Home Value</label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <input
                            type="text"
                            name="homeValue"
                            value={formData.homeValue}
                            onChange={handleChange}
                            className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                            placeholder="300,000"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Year Built</label>
                        <input
                          type="text"
                          name="yearBuilt"
                          value={formData.yearBuilt}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                          placeholder="2010"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Is this your primary residence?</label>
                        <select
                          name="isPrimaryResidence"
                          value={formData.isPrimaryResidence}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                          required
                        >
                          <option value="">Select</option>
                          <option value="yes">Yes - Primary Residence</option>
                          <option value="secondary">No - Secondary/Vacation Home</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Is property rented to others?</label>
                        <select
                          name="isRentedToOthers"
                          value={formData.isRentedToOthers}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                          required
                        >
                          <option value="">Select</option>
                          <option value="no">No</option>
                          <option value="yes">Yes - Rented</option>
                          <option value="airbnb">Yes - AirBnB/VRBO</option>
                        </select>
                      </div>
                    </div>
                  </>
                )}

                {formData.insuranceType === "car" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Vehicle Year</label>
                      <input
                        type="text"
                        name="vehicleYear"
                        value={formData.vehicleYear}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                        placeholder="2022"
                        required
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Make</label>
                        <input
                          type="text"
                          name="vehicleMake"
                          value={formData.vehicleMake}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                          placeholder="Toyota"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Model</label>
                        <input
                          type="text"
                          name="vehicleModel"
                          value={formData.vehicleModel}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                          placeholder="Camry"
                          required
                        />
                      </div>
                    </div>
                  </>
                )}

                {formData.insuranceType === "life" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Coverage Amount</label>
                      <select
                        name="lifeCoverageAmount"
                        value={formData.lifeCoverageAmount}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                        required
                      >
                        <option value="">Select coverage amount</option>
                        <option value="100000">$100,000</option>
                        <option value="250000">$250,000</option>
                        <option value="500000">$500,000</option>
                        <option value="1000000">$1,000,000</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Coverage Term</label>
                      <select
                        name="coverageTerm"
                        value={formData.coverageTerm}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                        required
                      >
                        <option value="">Select term length</option>
                        <option value="10">10 Years</option>
                        <option value="20">20 Years</option>
                        <option value="30">30 Years</option>
                      </select>
                    </div>
                  </>
                )}
              </div>

              <div className="flex gap-4 mt-8">
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex-1 py-4 border-2 border-slate-200 text-slate-600 font-semibold rounded-xl hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className="flex-1 py-4 bg-gradient-to-r from-navy-600 to-navy-700 text-white font-semibold rounded-xl hover:from-navy-700 hover:to-navy-800 transition-all flex items-center justify-center gap-2"
                >
                  Continue
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Homeowners Dwelling Details */}
          {step === 4 && formData.insuranceType === "homeowners" && (
            <div className="bg-white rounded-2xl p-8 shadow-lg" data-testid="quote-step-dwelling">
              <h2 className="text-2xl font-bold text-navy-900 mb-2">Dwelling Information</h2>
              <p className="text-slate-600 mb-8">Tell us more about your home's structure and systems.</p>
              
              <div className="space-y-6">
                {/* Construction Details */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <h3 className="font-semibold text-navy-900 mb-4">Construction Details</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Attached or Detached?</label>
                      <select
                        name="attachedOrDetached"
                        value={formData.attachedOrDetached}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                      >
                        <option value="">Select</option>
                        <option value="detached">Detached</option>
                        <option value="attached">Attached</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Construction Type</label>
                      <select
                        name="constructionType"
                        value={formData.constructionType}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                      >
                        <option value="">Select</option>
                        <option value="frame">Frame</option>
                        <option value="masonry">Masonry</option>
                        <option value="fire-resistive">Fire Resistive</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Number of Families</label>
                      <select
                        name="numberOfFamilies"
                        value={formData.numberOfFamilies}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                      >
                        <option value="">Select</option>
                        <option value="1">1 Family</option>
                        <option value="2">2 Family</option>
                        <option value="3">3 Family</option>
                        <option value="4">4 Family</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Number of Bathrooms</label>
                      <input
                        type="text"
                        name="numberOfBathrooms"
                        value={formData.numberOfBathrooms}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                        placeholder="e.g., 2.5"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Square Footage</label>
                      <input
                        type="text"
                        name="squareFootage"
                        value={formData.squareFootage}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                        placeholder="e.g., 2500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Number of Floors</label>
                      <select
                        name="numberOfFloors"
                        value={formData.numberOfFloors}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                      >
                        <option value="">Select</option>
                        <option value="1">1 Story</option>
                        <option value="1.5">1.5 Stories</option>
                        <option value="2">2 Stories</option>
                        <option value="2.5">2.5 Stories</option>
                        <option value="3">3+ Stories</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Roof & Exterior */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <h3 className="font-semibold text-navy-900 mb-4">Roof & Exterior</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Roof Covering</label>
                      <select
                        name="roofCovering"
                        value={formData.roofCovering}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                      >
                        <option value="">Select</option>
                        <option value="asphalt">Asphalt Shingles</option>
                        <option value="tile">Tile</option>
                        <option value="slate">Slate</option>
                        <option value="rubber">Rubber/Flat</option>
                        <option value="metal">Metal</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Roof Age (years)</label>
                      <input
                        type="text"
                        name="roofAge"
                        value={formData.roofAge}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                        placeholder="e.g., 10"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Siding Type</label>
                      <select
                        name="sidingType"
                        value={formData.sidingType}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                      >
                        <option value="">Select</option>
                        <option value="vinyl">Vinyl</option>
                        <option value="aluminum">Aluminum</option>
                        <option value="wood">Wood</option>
                        <option value="brick">Brick</option>
                        <option value="stucco">Stucco</option>
                        <option value="fiber-cement">Fiber Cement</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Garage</label>
                      <select
                        name="garageType"
                        value={formData.garageType}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                      >
                        <option value="">Select</option>
                        <option value="attached">Attached</option>
                        <option value="detached">Detached</option>
                        <option value="none">No Garage</option>
                        <option value="carport">Carport</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Foundation & Basement */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <h3 className="font-semibold text-navy-900 mb-4">Foundation</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Foundation Type</label>
                      <select
                        name="foundationType"
                        value={formData.foundationType}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                      >
                        <option value="">Select</option>
                        <option value="basement">Basement</option>
                        <option value="crawlspace">Crawlspace</option>
                        <option value="slab">Slab</option>
                        <option value="piers">Piers/Posts/Pilings</option>
                      </select>
                    </div>
                    {formData.foundationType === "basement" && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">% Basement Finished</label>
                          <input
                            type="text"
                            name="basementFinishedPercent"
                            value={formData.basementFinishedPercent}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                            placeholder="e.g., 50%"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Sump Pump?</label>
                          <select
                            name="hasSumpPump"
                            value={formData.hasSumpPump}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                          >
                            <option value="">Select</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Heating System */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <h3 className="font-semibold text-navy-900 mb-4">Heating System</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Heat Type</label>
                      <select
                        name="heatType"
                        value={formData.heatType}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                      >
                        <option value="">Select</option>
                        <option value="gas">Gas</option>
                        <option value="oil">Oil</option>
                        <option value="electric">Electric</option>
                        <option value="heat-pump">Heat Pump</option>
                        <option value="propane">Propane</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Burner/Furnace Age (years)</label>
                      <input
                        type="text"
                        name="burnerAge"
                        value={formData.burnerAge}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                        placeholder="e.g., 10"
                      />
                    </div>
                    {formData.heatType === "oil" && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Oil Tank Age (years)</label>
                          <input
                            type="text"
                            name="oilTankAge"
                            value={formData.oilTankAge}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                            placeholder="e.g., 15"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Oil Tank Material</label>
                          <select
                            name="oilTankConstruction"
                            value={formData.oilTankConstruction}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                          >
                            <option value="">Select</option>
                            <option value="steel">Steel</option>
                            <option value="fiberglass">Fiberglass</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Oil Tank Location</label>
                          <select
                            name="oilTankLocation"
                            value={formData.oilTankLocation}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                          >
                            <option value="">Select</option>
                            <option value="basement">Basement</option>
                            <option value="above-ground-outside">Above Ground - Outside</option>
                            <option value="underground">Underground</option>
                          </select>
                        </div>
                      </>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Alternate Heat Source?</label>
                      <select
                        name="hasAlternateHeatSource"
                        value={formData.hasAlternateHeatSource}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                      >
                        <option value="">Select</option>
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </div>
                    {formData.hasAlternateHeatSource === "yes" && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Type of Alternate Heat</label>
                        <select
                          name="alternateHeatType"
                          value={formData.alternateHeatType}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                        >
                          <option value="">Select</option>
                          <option value="wood-stove">Wood Stove</option>
                          <option value="pellet-stove">Pellet Stove</option>
                          <option value="gas-fireplace">Gas Fireplace</option>
                          <option value="wood-fireplace">Wood Fireplace</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                {/* Electrical & Plumbing */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <h3 className="font-semibold text-navy-900 mb-4">Electrical & Plumbing</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Electrical Type</label>
                      <select
                        name="electricalType"
                        value={formData.electricalType}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                      >
                        <option value="">Select</option>
                        <option value="circuit-breakers">Circuit Breakers</option>
                        <option value="fuses">Fuses</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Year of Last Electrical Update</label>
                      <input
                        type="text"
                        name="electricalUpdateYear"
                        value={formData.electricalUpdateYear}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                        placeholder="e.g., 2015"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Year of Last Plumbing Update</label>
                      <input
                        type="text"
                        name="plumbingUpdateYear"
                        value={formData.plumbingUpdateYear}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                        placeholder="e.g., 2018"
                      />
                    </div>
                  </div>
                </div>

                {/* Safety & Liability */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <h3 className="font-semibold text-navy-900 mb-4">Safety & Liability</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Do you have dogs?</label>
                      <select
                        name="hasDogs"
                        value={formData.hasDogs}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                      >
                        <option value="">Select</option>
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </div>
                    {formData.hasDogs === "yes" && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Dog Breed(s)</label>
                        <input
                          type="text"
                          name="dogBreeds"
                          value={formData.dogBreeds}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                          placeholder="e.g., Labrador, Golden Retriever"
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Trampolines or Skateboard Ramps?</label>
                      <select
                        name="hasTrampolines"
                        value={formData.hasTrampolines}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                      >
                        <option value="">Select</option>
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Swimming Pool?</label>
                      <select
                        name="hasPool"
                        value={formData.hasPool}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                      >
                        <option value="">Select</option>
                        <option value="no">No</option>
                        <option value="inground">Yes - In-ground</option>
                        <option value="above-ground">Yes - Above Ground</option>
                      </select>
                    </div>
                    {(formData.hasPool === "inground" || formData.hasPool === "above-ground") && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Pool has Diving Board?</label>
                          <select
                            name="poolHasDivingBoard"
                            value={formData.poolHasDivingBoard}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                          >
                            <option value="">Select</option>
                            <option value="no">No</option>
                            <option value="yes">Yes</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Pool has Slide?</label>
                          <select
                            name="poolHasSlide"
                            value={formData.poolHasSlide}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                          >
                            <option value="">Select</option>
                            <option value="no">No</option>
                            <option value="yes">Yes</option>
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Planned Renovations */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Any planned renovations within the next year?</label>
                    <select
                      name="hasPlannedRenovations"
                      value={formData.hasPlannedRenovations}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                    >
                      <option value="">Select</option>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                  {formData.hasPlannedRenovations === "yes" && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Renovation Details</label>
                      <input
                        type="text"
                        name="renovationDetails"
                        value={formData.renovationDetails}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                        placeholder="Brief description of planned work"
                      />
                    </div>
                  )}
                </div>

                {/* Loss History */}
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <h3 className="font-semibold text-navy-900 mb-4">Loss History</h3>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Any insurance claims in the last 6 years?</label>
                    <select
                      name="hasLossHistory"
                      value={formData.hasLossHistory}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                    >
                      <option value="">Select</option>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                  {formData.hasLossHistory === "yes" && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-slate-700 mb-2">Please provide details (date, type of loss, amount)</label>
                      <textarea
                        name="lossHistoryDetails"
                        value={formData.lossHistoryDetails}
                        onChange={handleChange}
                        rows={3}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
                        placeholder="e.g., Water damage - March 2022 - $5,000"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex-1 py-4 border-2 border-slate-200 text-slate-600 font-semibold rounded-xl hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className="flex-1 py-4 bg-gradient-to-r from-navy-600 to-navy-700 text-white font-semibold rounded-xl hover:from-navy-700 hover:to-navy-800 transition-all flex items-center justify-center gap-2"
                >
                  Continue
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Document Upload (for non-life, non-homeowners) OR Step 5 for Homeowners */}
          {((step === 4 && formData.insuranceType !== "life" && formData.insuranceType !== "homeowners") || 
            (step === 5 && formData.insuranceType === "homeowners")) && (
            <div className="bg-white rounded-2xl p-8 shadow-lg" data-testid="quote-step-4">
              <h2 className="text-2xl font-bold text-navy-900 mb-2">{getDocumentRequirements().title}</h2>
              <p className="text-slate-600 mb-6">{getDocumentRequirements().description}</p>
              
              {/* Suggested Documents */}
              <div className="bg-blue-50 rounded-xl p-4 mb-6">
                <h3 className="font-medium text-navy-900 mb-2 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Suggested Documents
                </h3>
                <ul className="text-sm text-slate-600 space-y-1">
                  {getDocumentRequirements().suggestions.map((suggestion, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-500" />
                      {suggestion}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Upload Area */}
              <div
                className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-navy-400 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  className="hidden"
                  data-testid="file-upload-input"
                />
                <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-navy-900 font-medium mb-2">Click to upload or drag and drop</p>
                <p className="text-sm text-slate-500">PDF, JPG, PNG, DOC up to 10MB each</p>
              </div>

              {/* Uploaded Files List */}
              {uploadedFiles.length > 0 && (
                <div className="mt-6 space-y-3">
                  <h3 className="font-medium text-navy-900">Uploaded Documents ({uploadedFiles.length})</h3>
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <File className="w-5 h-5 text-navy-600" />
                        <div>
                          <p className="text-sm font-medium text-navy-900">{file.name}</p>
                          <p className="text-xs text-slate-500">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="p-1 hover:bg-slate-200 rounded-full transition-colors"
                      >
                        <X className="w-5 h-5 text-slate-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Skip Option */}
              <p className="text-center text-sm text-slate-500 mt-4">
                Documents are optional but help us provide more accurate quotes.
              </p>

              <div className="flex gap-4 mt-8">
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex-1 py-4 border-2 border-slate-200 text-slate-600 font-semibold rounded-xl hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className="flex-1 py-4 bg-gradient-to-r from-navy-600 to-navy-700 text-white font-semibold rounded-xl hover:from-navy-700 hover:to-navy-800 transition-all flex items-center justify-center gap-2"
                >
                  {uploadedFiles.length > 0 ? "Continue" : "Skip & Continue"}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 5 (or 4 for life, or 6 for homeowners): Review & Submit */}
          {((step === 5 && formData.insuranceType !== "life" && formData.insuranceType !== "homeowners") || 
            (step === 4 && formData.insuranceType === "life") ||
            (step === 6 && formData.insuranceType === "homeowners")) && (
            <div className="bg-white rounded-2xl p-8 shadow-lg" data-testid="quote-step-review">
              <h2 className="text-2xl font-bold text-navy-900 mb-2">Review Your Information</h2>
              <p className="text-slate-600 mb-8">Please review your details before submitting.</p>
              
              <div className="space-y-6">
                <div className="p-4 bg-slate-50 rounded-xl">
                  <h3 className="font-semibold text-navy-900 mb-3">Insurance Type</h3>
                  <p className="text-slate-600 capitalize">{formData.insuranceType} Insurance</p>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl">
                  <h3 className="font-semibold text-navy-900 mb-3">Personal Information</h3>
                  <div className="grid sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Name:</span>
                      <span className="ml-2 text-slate-700">{formData.firstName} {formData.lastName}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Email:</span>
                      <span className="ml-2 text-slate-700">{formData.email}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Phone:</span>
                      <span className="ml-2 text-slate-700">{formData.phone}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">ZIP Code:</span>
                      <span className="ml-2 text-slate-700">{formData.zipCode}</span>
                    </div>
                  </div>
                </div>

                {uploadedFiles.length > 0 && (
                  <div className="p-4 bg-emerald-50 rounded-xl">
                    <h3 className="font-semibold text-navy-900 mb-3 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      Documents Uploaded
                    </h3>
                    <ul className="text-sm text-slate-600 space-y-1">
                      {uploadedFiles.map((file, index) => (
                        <li key={index} className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-emerald-600" />
                          {file.name} ({formatFileSize(file.size)})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="p-4 bg-navy-50 rounded-xl border border-navy-200">
                  <p className="text-sm text-navy-700">
                    By submitting this form, you agree to be contacted by Bradley Greenberg regarding your insurance quote. 
                    Your information is secure and will never be sold to third parties.
                  </p>
                </div>

                {submitError && (
                  <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                    <p className="text-sm text-red-700">{submitError}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-4 mt-8">
                <button
                  type="button"
                  onClick={prevStep}
                  disabled={isSubmitting}
                  className="flex-1 py-4 border-2 border-slate-200 text-slate-600 font-semibold rounded-xl hover:bg-slate-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-amber-700 transition-all flex items-center justify-center gap-2 disabled:opacity-70"
                  data-testid="quote-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Quote Request
                      <CheckCircle2 className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default QuotePage;
